import { useMemo } from "react";
import { KPISlice } from "../service/DataProcessorService";
import { StatusCard } from "../statusCard";
import { useLeadFilters } from "@/modules/leads/hooks/useLeadFilters";
import { useLeadsByCity } from "../hooks/useLeadsByCity";
import { useDashboardLeads } from "@/common/states/store/slices/dashboardSlice";
import { useGetCompanies } from "@/common/hooks";
import { brasiliaTimeHour } from "@/lib/utils";
import { getCompanyIdByName } from "@/common/helpers/company.helpers";
import type { LeadsByCompany } from "../hooks/useDashLead";

interface KPISectionProps {
  className?: string;
  type: string;
  title: string;
  subtitle?: string;
  kpis: KPISlice;
  activations?: number;
  reactivations?: number;
  tma?: number;
  dailyGoal?: number;
  monthGoal?: number;
  projectionUntilToday?: { label: string; value: number };
  loading?: boolean;
  onTotalLeadsClick?: (city?: string) => void;
  leadsByTime?: Record<string, number>;
  leadsByCompany?: LeadsByCompany[];
}

export function KPISection({
  className,
  type,
  title,
  subtitle,
  kpis,
  activations,
  reactivations,
  tma,
  dailyGoal,
  onTotalLeadsClick,
  projectionUntilToday,
  leadsByTime,
  leadsByCompany,
}: KPISectionProps) {
  const { filters } = useLeadFilters();
  const { quickFilter } = filters;
  const { allLeadsByCityEntries } = useLeadsByCity(leadsByTime);
  const { setModalStatus } = useDashboardLeads();
  const { data: companies } = useGetCompanies();
  const todayLeadsByCompanyEntries = useMemo(
    () =>
      Object.values(
        (leadsByCompany ?? []).reduce((acc, item) => {
          const companyName = item.companyName?.trim();

          if (!companyName) {
            return acc;
          }

          if (!acc[companyName]) {
            acc[companyName] = [companyName, 0] as [string, number];
          }

          acc[companyName][1] += item.count ?? 0;
          return acc;
        }, {} as Record<string, [string, number]>)
      ).sort(([, a], [, b]) => b - a),
    [leadsByCompany]
  );

  const cityEntries =
    type === "today" && todayLeadsByCompanyEntries.length > 0
      ? todayLeadsByCompanyEntries
      : allLeadsByCityEntries;

  const handleOpenModalFilters = (status?: string) => {
    setModalStatus(status as string);
    onTotalLeadsClick?.();
  };

  const cityWithDelayedGoal = (
    companyName: string,
    goalCurrent: number
  ): boolean => {
    const company = companies?.find(
      (companie) => companie._id == getCompanyIdByName(companyName)
    );

    const goal = company?.leadGoals?.find(
      (goal) => goal.hour === brasiliaTimeHour
    );

    return Number(goal?.goalQuantity) > goalCurrent;
  };

  return (
    <section className={className}>
      <h3 className="text-base sm:text-lg font-semibold text-gray-500 mb-3 sm:mb-4">
        {title} <span className="text-base text-gray2">{subtitle}</span>
      </h3>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        <StatusCard
          title="Total de Leads"
          value={kpis.totalLeadsCount ?? 0}
          color="text-gray-600"
          onNumberClick={onTotalLeadsClick}
        />
        {type === "today" && kpis.leadsTodayWithoutCity !== undefined && (
          <StatusCard
            title="Leads sem Cidade"
            value={kpis.leadsTodayWithoutCity ?? 0}
            color="text-orange-600"
          />
        )}
        {/* 
          O motivo de usamos a propriedade TYPE é porque
          não vamos exibir os cards de "HOJE" temporariamente 
          
          Vamos exibir os leads por cidade no lugar
        */}
        {(leadsByTime !== undefined || leadsByCompany !== undefined) &&
          cityEntries.map((companie) => {
            return (
              <StatusCard
                className="capitalize"
                key={companie[0]}
                title={companie[0]}
                value={companie[1]}
                delayed={cityWithDelayedGoal(companie[0], companie[1])}
                onNumberClick={() => onTotalLeadsClick?.(companie[0])}
              />
            );
          })}
        {type === "period" && (
          <StatusCard
            title="Novos Leads"
            value={kpis.leadsCountByStatus?.new_lead ?? 0}
            color="text-locavibe"
            onNumberClick={() => handleOpenModalFilters("new_lead")}
          />
        )}
        {type === "period" && (
          <StatusCard
            title="Em Negociação"
            value={kpis.leadsCountByStatus?.incomplete ?? 0}
            color="text-yellow-600"
            onNumberClick={() => handleOpenModalFilters("incomplete")}
          />
        )}
        {type === "period" && (
          <StatusCard
            title="Em Tratativa"
            value={kpis.leadsCountByStatus?.under_negotiation ?? 0}
            color="text-yellow-600"
            onNumberClick={() => handleOpenModalFilters("under_negotiation")}
          />
        )}
        {type === "period" && (
          <StatusCard
            title="Validados"
            value={kpis.leadsCountByStatus?.ready_to_rent ?? 0}
            color="text-green-600"
            onNumberClick={() => handleOpenModalFilters("ready_to_rent")}
          />
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {type === "period" && (
          <StatusCard
            title="Taxa de Conversão"
            value={kpis.conversionRate}
            color="text-black"
          />
        )}
        {activations !== undefined && (
          <StatusCard
            title="Ativações"
            value={activations.toLocaleString("pt-BR")}
            color="text-black"
          />
        )}
        {reactivations !== undefined && (
          <StatusCard
            title="Renovações"
            value={reactivations.toLocaleString("pt-BR")}
            color="text-black"
          />
        )}
        {dailyGoal !== undefined && (
          <StatusCard
            title="Meta do dia"
            value={dailyGoal}
            color="text-black"
            percentage={
              activations !== undefined ? (activations / dailyGoal) * 100 : 0
            }
          />
        )}
        {tma !== undefined && (
          <StatusCard
            title="TMA"
            value={Number(tma).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
            color="text-black"
          />
        )}
        {projectionUntilToday !== undefined && quickFilter === "thisMonth" && (
          <StatusCard
            title={projectionUntilToday.label}
            value={Number(projectionUntilToday.value)}
            color="text-black"
          />
        )}
        {/* {monthGoal !== undefined &&
          quickFilter === "thisMonth" && (
            <StatusCard
              title={"Meta mês"}
              value={monthGoal}
              color="text-black"
              percentage={activations !== undefined ? ((activations / monthGoal) * 100) : 0}
            />
          )} */}
      </div>
    </section>
  );
}
